/**
 * 服务器选择器下拉组件
 * 用在 ChatHeader 中，允许用户在不关闭聊天的情况下切换服务器。
 */

import React, { useEffect, useState, useRef } from 'react';
import { useIsLightUi } from '@/hooks';

interface ServerSelectorProps {
  currentServerId: number;
  currentServerName: string;
  currentServerIp: string;
  onSwitch: (serverId: number, name: string, ip: string) => void;
}

interface ServerItem {
  id: number;
  server_name: string;
  ip_address: string;
  provider?: string;
}

export const ServerSelector: React.FC<ServerSelectorProps> = ({
  currentServerId,
  currentServerName,
  currentServerIp: _currentServerIp,
  onSwitch,
}) => {
  const [open, setOpen] = useState(false);
  const [servers, setServers] = useState<ServerItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const isLightUi = useIsLightUi();

  // 点击外部关闭
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const loadServers = async () => {
    if (loaded) return;
    setLoading(true);
    try {
      const nonce = (window as any).sseConfig?.nonce || '';
      const ajaxUrl = (window as any).sseConfig?.ajax_url || '/wp-admin/admin-ajax.php';
      if (!nonce) {
        setServers([]);
        return;
      }
      const formData = new FormData();
      formData.append('action', 'sse_get_server_list');
      formData.append('nonce', nonce);
      const resp = await fetch(ajaxUrl, { method: 'POST', body: formData });
      const json = await resp.json();
      if (json.success && json.data?.servers) {
        setServers(json.data.servers);
        setLoaded(true);
      } else {
        setServers([]);
      }
    } catch {
      setServers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    setOpen((v) => !v);
    if (!open && !loaded) loadServers();
  };

  const handleSelect = (s: ServerItem) => {
    if (s.id === currentServerId) {
      setOpen(false);
      return;
    }
    onSwitch(s.id, s.server_name || '未知服务器', s.ip_address || '-');
    setOpen(false);
  };

  const bg = isLightUi ? '#fff' : '#2d2d2d';
  const border = isLightUi ? '#e5e7eb' : '#444';
  const hoverBg = isLightUi ? '#f5f5f5' : '#3a3a3a';
  const textPrimary = isLightUi ? '#1e1e1e' : '#e0e0e0';
  const textSecondary = isLightUi ? '#999' : '#aaa';

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={handleToggle}
        title="切换服务器"
        style={{
          background: 'none',
          border: 'none',
          color: 'inherit',
          cursor: 'pointer',
          fontSize: 13,
          padding: '2px 6px',
          borderRadius: 4,
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          opacity: 0.8,
        }}
      >
        <span style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {currentServerName}
        </span>
        <span style={{ fontSize: 10, opacity: 0.6 }}>{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: 4,
            background: bg,
            border: `1px solid ${border}`,
            borderRadius: 8,
            boxShadow: '0 4px 16px rgba(0,0,0,.12)',
            width: 260,
            maxHeight: 300,
            overflowY: 'auto',
            zIndex: 999999,
          }}
        >
          {loading && (
            <div style={{ padding: '16px', textAlign: 'center', color: textSecondary, fontSize: 13 }}>
              加载中…
            </div>
          )}
          {!loading && servers.length === 0 && (
            <div style={{ padding: '16px', textAlign: 'center', color: textSecondary, fontSize: 13 }}>
              暂无可用服务器
            </div>
          )}
          {servers.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => handleSelect(s)}
              style={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                padding: '8px 12px',
                border: 'none',
                background: s.id === currentServerId ? (isLightUi ? '#eef2ff' : '#1e1e3a') : 'transparent',
                cursor: 'pointer',
                textAlign: 'left',
                gap: 8,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = hoverBg)}
              onMouseLeave={(e) =>
                (e.currentTarget.style.background =
                  s.id === currentServerId ? (isLightUi ? '#eef2ff' : '#1e1e3a') : 'transparent')
              }
            >
              <span style={{ fontSize: 16, flexShrink: 0 }}>{getProviderIcon(s.provider)}</span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 13, color: textPrimary, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {s.server_name || '未知服务器'}
                </div>
                <div style={{ fontSize: 11, color: textSecondary, marginTop: 1 }}>{s.ip_address || '-'}</div>
              </div>
              {s.id === currentServerId && <span style={{ color: '#6366f1', fontSize: 14 }}>✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

function getProviderIcon(provider?: string): string {
  switch (provider) {
    case 'tencent': return '💠';
    case 'aliyun': return '☁️';
    case 'zy': return '🐟';
    default: return '🖥️';
  }
}
